import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiEndpoints } from '../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import {
  Package,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Download,
  RefreshCw,
  MapPin,
  Truck,
  Box,
  FileText
} from 'lucide-react';

const InventoryReports = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [inventoryStats, setInventoryStats] = useState({
    totalItems: 0,
    totalValue: 0,
    lowStockItems: 0,
    outOfStockItems: 0,
    categories: {},
    monthlyUsage: [],
    topUsedItems: [],
    stockLevels: [],
    categoryDistribution: {},
    supplierDistribution: {},
    locationDistribution: {}
  });

  const isRTL = i18n.language === 'ar';

  const generateMonthlyUsageData = useCallback(() => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = date.toLocaleDateString(i18n.language, { month: 'short' });
      
      const usage = Math.floor(Math.random() * 100) + 20;
      const additions = Math.floor(Math.random() * 50) + 10;
      
      months.push({
        month: monthName,
        usage: usage,
        additions: additions,
        netChange: additions - usage
      });
    }
    
    return months;
  }, [i18n.language]);

  const fetchInventoryReportData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiEndpoints.reports.getInventoryReport();
      const data = response.data;

      const monthlyUsage = generateMonthlyUsageData();

      setInventoryStats({
        totalItems: data.totalItems || 0,
        totalValue: data.totalValue || 0,
        lowStockItems: data.lowStockItems || 0,
        outOfStockItems: data.outOfStockItems || 0,
        categories: data.categories || {},
        monthlyUsage,
        topUsedItems: data.topUsedItems || [],
        stockLevels: data.stockLevels || [
          { level: t('common.active'), count: data.totalItems - (data.lowStockItems + data.outOfStockItems), color: '#10B981' },
          { level: t('reports.lowStockItems'), count: data.lowStockItems, color: '#F59E0B' },
          { level: t('reports.outOfStockItems'), count: data.outOfStockItems, color: '#EF4444' }
        ],
        categoryDistribution: data.categoryDistribution || {},
        supplierDistribution: data.supplierDistribution || {},
        locationDistribution: data.locationDistribution || {}
      });

    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  }, [generateMonthlyUsageData, t]);

  useEffect(() => {
    fetchInventoryReportData();
  }, [fetchInventoryReportData, dateRange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-DZ' : 'en-US', {
      style: 'currency',
      currency: 'DZD',
      maximumFractionDigits: 0
    }).format(value).replace('DZD', 'DA');
  };

  const exportInventoryReport = () => {
    const data = {
      summary: {
        totalItems: inventoryStats.totalItems,
        totalValue: inventoryStats.totalValue,
        lowStockItems: inventoryStats.lowStockItems,
        outOfStockItems: inventoryStats.outOfStockItems
      },
      categories: inventoryStats.categories,
      categoryDistribution: inventoryStats.categoryDistribution,
      monthlyUsage: inventoryStats.monthlyUsage,
      topUsedItems: inventoryStats.topUsedItems,
      generatedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6', '#14B8A6'];

  if (loading && !inventoryStats.totalItems) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 pb-20 ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('reports.inventoryReports')}</h1>
          <p className="text-gray-500">{t('reports.inventoryReportsSubtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm font-medium shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="week">{t('reports.lastWeek')}</option>
            <option value="month">{t('reports.lastMonth')}</option>
            <option value="quarter">{t('reports.lastQuarter')}</option>
            <option value="year">{t('reports.lastYear')}</option>
          </select>
          <button
            onClick={exportInventoryReport}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 shadow-sm transition-all text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t('reports.exportReport')}</span>
          </button>
          <button
            onClick={() => fetchInventoryReportData()}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 shadow-sm transition-all text-gray-700 text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{t('reports.refresh')}</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('reports.totalItems')}
          value={inventoryStats.totalItems}
          icon={<Package className="text-blue-500" />}
          bg="bg-blue-50"
          color="text-blue-700"
        />
        <StatCard
          title={t('reports.totalValue')}
          value={formatCurrency(inventoryStats.totalValue)}
          icon={<TrendingUp className="text-emerald-500" />}
          bg="bg-emerald-50"
          color="text-emerald-700"
        />
        <StatCard
          title={t('reports.lowStockItems')}
          value={inventoryStats.lowStockItems}
          icon={<AlertTriangle className="text-amber-500" />}
          bg="bg-amber-50"
          color="text-amber-700"
        />
        <StatCard
          title={t('reports.outOfStockItems')}
          value={inventoryStats.outOfStockItems}
          icon={<TrendingDown className="text-rose-500" />}
          bg="bg-rose-50"
          color="text-rose-700"
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Usage Trend */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-6">{t('reports.monthlyUsage')}</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={inventoryStats.monthlyUsage}>
                <defs>
                  <linearGradient id="colorUsage" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorAdd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 12}} />
                <Tooltip />
                <Legend verticalAlign="top" height={36} />
                <Area 
                  type="monotone" 
                  dataKey="usage" 
                  name={t('reports.usage')} 
                  stroke="#3B82F6" 
                  strokeWidth={3} 
                  fillOpacity={1} 
                  fill="url(#colorUsage)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="additions" 
                  name={t('reports.additions')} 
                  stroke="#10B981" 
                  strokeWidth={3} 
                  fillOpacity={1} 
                  fill="url(#colorAdd)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-6">{t('reports.categoryDistribution')}</h3>
          <div className="h-[250px] w-full mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(inventoryStats.categoryDistribution).map(([name, value]) => ({ name, value }))}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {Object.entries(inventoryStats.categoryDistribution).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {Object.entries(inventoryStats.categoryDistribution).slice(0, 4).map(([name, value], idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></span>
                  <span className="text-gray-600 truncate max-w-[120px]">{name}</span>
                </div>
                <span className="font-semibold text-gray-800">{formatCurrency(value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stock Levels & Top Items */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stock Analysis */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <h3 className="text-lg font-bold text-gray-800 mb-6">{t('reports.stockLevelsAnalysis')}</h3>
          <div className="space-y-4">
            {inventoryStats.stockLevels.map((level, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 rounded-xl border border-gray-50 bg-gray-50/30">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-inner" style={{ backgroundColor: `${level.color}20` }}>
                  {level.level === t('reports.outOfStockItems') ? (
                    <TrendingDown className="w-6 h-6" style={{ color: level.color }} />
                  ) : level.level === t('reports.lowStockItems') ? (
                    <AlertTriangle className="w-6 h-6" style={{ color: level.color }} />
                  ) : (
                    <CheckCircle className="w-6 h-6" style={{ color: level.color }} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">{level.level}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-black text-gray-800">{level.count}</span>
                    <span className="text-xs px-2 py-1 rounded bg-white border border-gray-100 text-gray-500">
                      {((level.count / (inventoryStats.totalItems || 1)) * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Used Items */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-50 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-800">{t('reports.topUsedItems')}</h3>
            <Box className="w-5 h-5 text-blue-500" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left rtl:text-right">
              <thead className="bg-gray-50/50 text-gray-500 uppercase text-[10px] font-bold tracking-widest">
                <tr>
                  <th className="px-6 py-4">{t('reports.itemName')}</th>
                  <th className="px-6 py-4">{t('reports.category')}</th>
                  <th className="px-6 py-4">{t('reports.quantity')}</th>
                  <th className="px-6 py-4">{t('reports.minimumQuantity')}</th>
                  <th className="px-6 py-4">{t('reports.status')}</th>
                  <th className="px-6 py-4 text-right">{t('reports.value')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {inventoryStats.topUsedItems.length > 0 ? (
                  inventoryStats.topUsedItems.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-bold text-gray-900">
                        <div className="flex items-center gap-2">
                          {item.isLowStock && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                          {item.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-[11px] font-bold">
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-600">{item.quantity}</td>
                      <td className="px-6 py-4 font-medium text-gray-400">{item.minimumQuantity}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                          item.isLowStock 
                            ? 'bg-amber-50 text-amber-600 border border-amber-100' 
                            : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                        }`}>
                          {item.isLowStock ? t('reports.lowStockStatus') : t('reports.adequateStatus')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-black text-gray-800">
                        {formatCurrency(item.value)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400 italic">
                      {t('reports.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Distribution Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Supplier Distribution */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <Truck className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-bold text-gray-800">{t('reports.supplierDistribution')}</h3>
          </div>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(inventoryStats.supplierDistribution).map(([name, value]) => ({ name, value }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 10}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 10}} />
                <Tooltip />
                <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Location Distribution */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-5 h-5 text-indigo-500" />
            <h3 className="text-lg font-bold text-gray-800">{t('reports.locationDistribution')}</h3>
          </div>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(inventoryStats.locationDistribution).map(([name, value]) => ({ name, value }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 10}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#9CA3AF', fontSize: 10}} />
                <Tooltip />
                <Bar dataKey="value" fill="#6366F1" radius={[4, 4, 0, 0]} barSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, bg, color }) => (
  <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{title}</span>
      <div className={`p-2 rounded-xl ${bg}`}>
        {React.cloneElement(icon, { size: 18 })}
      </div>
    </div>
    <div className={`text-xl font-black ${color}`}>
      {value}
    </div>
  </div>
);

export default InventoryReports;

